import {  useEffect } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import { useRegistration } from '@/context/RegistrationContext';
import { getCmsEligibleTracks } from '@/utils/eligibility';

type FlowCalculationFunction = (context: ReturnType<typeof useRegistration>) => boolean;

export const STEP_KEYS = {
  LANDING: 'Landing',
  PROMO_CODE_CAP_REACHED: 'PromoCodeCapReached',
  EMAIL_SENT: 'EmailSent',
  MEDICARE_COVERAGE: 'MedicareCoverage',
  ECKM_CONDITIONS: 'ECKMConditions',
  CKM_CONDITIONS: 'CKMConditions',
  BEHAVIORAL_HEALTH: 'BehavioralHealth',
  PHQ9_SCREENER: 'PHQ9Screener',
  GAD7_SCREENER: 'GAD7Screener',
  ELIGIBILITY_RESULT: 'EligibilityResult',
  TRACK_OFFERING: 'TrackOffering',
  NOT_ELIGIBLE: 'NotEligible',
  CREATE_ACCOUNT: 'CreateAccount',
  SHIPPING: 'Shipping',
  CHECK_SMS: 'CheckSMS',
  DOWNLOAD_APP: 'DownloadApp',
  DOWNLOAD_APP_QR: 'DownloadAppQR',
} as const;

type StepName = (typeof STEP_KEYS)[keyof typeof STEP_KEYS];

type Step = {
  name: StepName;
  isRequired: FlowCalculationFunction;
  isCompleted: FlowCalculationFunction;
  isAccessible: FlowCalculationFunction;
  route: string;
  ignoreProgress?: boolean;
  blockBackNavigation?: boolean;
};

const d =
  (defaultValue: boolean): FlowCalculationFunction =>
  () =>
    Boolean(defaultValue);

// RND-35692: the PHQ-9/GAD-7 screeners run only when depression/anxiety was
// reported on the Behavioral Health step.
const hasBHCondition: FlowCalculationFunction = ({ registrationData }) =>
  registrationData.behavioralHealthConditions.selectedConditions.has('depression') ||
  registrationData.behavioralHealthConditions.selectedConditions.has('anxiety');

// GAD-7 must not be reachable until PHQ-9 is done — PHQ-9's Q9 crisis gate has
// to fire first, and GAD7ScreenerStep falls back to phq9Score ?? 0 otherwise.
const hasCompletedPHQ9: FlowCalculationFunction = ({ registrationData }) =>
  registrationData.bhScreener.phq9Score !== null;

// RND-36523: the CMS $check-eligibility API (run up front, right after personal
// info) is the source of truth for which tracks the patient may enroll in.
// Derived from the per-track results; drives condition-screen filtering,
// screener gating, and the early exits.
const cmsEligible = (ctx: ReturnType<typeof useRegistration>) =>
  new Set(getCmsEligibleTracks(ctx.registrationData.eligibilityResults ?? []));

const stepsConfiguration: Step[] = [
  {
    name: STEP_KEYS.LANDING,
    isRequired: d(false),
    isCompleted: d(true),
    isAccessible: d(true),
    route: '/',
    ignoreProgress: true,
  },
  {
    name: STEP_KEYS.PROMO_CODE_CAP_REACHED,
    isRequired: d(false),
    isCompleted: d(true),
    isAccessible: d(true),
    route: '/cap-reached',
    blockBackNavigation: true,
    ignoreProgress: true,
  },
  {
    name: STEP_KEYS.EMAIL_SENT,
    isRequired: d(false),
    isCompleted: d(true),
    isAccessible: d(true),
    route: '/email-sent',
    blockBackNavigation: true,
    ignoreProgress: true,
  },
  {
    name: STEP_KEYS.MEDICARE_COVERAGE,
    isRequired: d(true),
    isCompleted: ({ registrationData }) =>  Boolean(
        registrationData.medicareCoverage.firstName &&
        registrationData.medicareCoverage.lastName &&
        registrationData.medicareCoverage.dateOfBirth &&
        registrationData.medicareCoverage.mbi &&
        registrationData.account.email &&
        registrationData.account.phoneNumber
      )
      ,
    isAccessible: d(true),
    route: '/medicare-coverage',
  },
  {
    name: STEP_KEYS.ELIGIBILITY_RESULT,
    // RND-36523: eligibility runs up front, right after MEDICARE_COVERAGE.
    // Required while CMS submissions exist and the final eligibility has not been
    // decided yet (eligibility stays null until the post-questionnaire finalize).
    isRequired: ({ registrationData }) =>
      (registrationData.eligibilityResults ?? []).length > 0 &&
      registrationData.eligibility === null,
    isCompleted: ({ registrationData }) => {
      const results = registrationData.eligibilityResults ?? [];
      // At least one result required; an empty set is NOT "complete" (vacuous true
      // would skip the step before any CMS result lands).
      return results.length > 0 && results.every(r => r.status === 'complete');
    },
    isAccessible: ({ registrationData }) =>
      Boolean(registrationData.medicareCoverage.mbi) &&
      (registrationData.eligibilityResults ?? []).length > 0,
    route: '/eligibility-result',
    blockBackNavigation: true,
    ignoreProgress: true,
  },
  {
    name: STEP_KEYS.ECKM_CONDITIONS,
    // RND-36523: only shown when CMS confirmed the eCKM track eligible.
    isRequired: (ctx) => cmsEligible(ctx).has('eCKM'),
    isCompleted: ({ registrationData }) =>
      registrationData.eCKMConditions.selectedConditions.size > 0 ||
      registrationData.eCKMConditions.completed === true ||
      registrationData.eligibility !== null,
    isAccessible: (ctx) =>
      Boolean(ctx.registrationData.medicareCoverage.mbi) && cmsEligible(ctx).has('eCKM'),
    route: '/health-conditions',
    blockBackNavigation: true,
  },
  {
    name: STEP_KEYS.CKM_CONDITIONS,
    // RND-36523: only shown when CMS confirmed the CKM track eligible.
    isRequired: (ctx) => cmsEligible(ctx).has('CKM'),
    isCompleted: ({ registrationData }) =>
      registrationData.ckmConditions.selectedConditions.size > 0 ||
      registrationData.ckmConditions.completed === true ||
      registrationData.eligibility !== null,
    isAccessible: (ctx) =>
      Boolean(ctx.registrationData.medicareCoverage.mbi) && cmsEligible(ctx).has('CKM'),
    route: '/ckm-conditions',
    blockBackNavigation: true,
  },
  {
    name: STEP_KEYS.BEHAVIORAL_HEALTH,
    // RND-36523: shown iff CMS confirmed the BH track eligible. The old
    // CKM-exclusion / eCKM-CKM-completed coupling is removed — BH visibility now
    // depends solely on the CMS eligibility result.
    isRequired: (ctx) => cmsEligible(ctx).has('BH'),
    isCompleted: ({ registrationData }) =>
      registrationData.behavioralHealthConditions.selectedConditions.size > 0 ||
      registrationData.behavioralHealthConditions.completed === true ||
      registrationData.eligibility !== null,
    isAccessible: (ctx) =>
      Boolean(ctx.registrationData.medicareCoverage.mbi) && cmsEligible(ctx).has('BH'),
    route: '/behavioral-health-conditions',
    blockBackNavigation: true,
  },
  {
    // RND-35692 / RND-36523: PHQ-9 screener — double-gated: only when CMS
    // confirmed BH eligible AND the patient reported depression/anxiety.
    name: STEP_KEYS.PHQ9_SCREENER,
    isRequired: (ctx) => cmsEligible(ctx).has('BH') && hasBHCondition(ctx),
    isCompleted: ({ registrationData }) => registrationData.bhScreener.phq9Score !== null,
    // Once the whole BH screener sequence is done, revisiting PHQ-9 would silently
    // overwrite an already-submitted phq9Score/crisisGateShown — block that.
    isAccessible: (ctx) =>
      cmsEligible(ctx).has('BH') &&
      hasBHCondition(ctx) &&
      ctx.registrationData.bhScreener.completed !== true,
    route: '/phq9-screener',
    blockBackNavigation: true,
  },
  {
    // RND-35692 / RND-36523: GAD-7 screener — double-gated like PHQ-9.
    name: STEP_KEYS.GAD7_SCREENER,
    isRequired: (ctx) => cmsEligible(ctx).has('BH') && hasBHCondition(ctx),
    isCompleted: ({ registrationData }) => registrationData.bhScreener.completed === true,
    isAccessible: (ctx) =>
      cmsEligible(ctx).has('BH') && hasBHCondition(ctx) && hasCompletedPHQ9(ctx),
    route: '/gad7-screener',
    blockBackNavigation: true,
  },
  {
    name: STEP_KEYS.TRACK_OFFERING,
    isRequired: ({ registrationData }) =>
      registrationData.eligibility?.isEligible === true,
    isCompleted: ({ registrationData }) =>
      Boolean(registrationData.selectedTracks?.length),
    isAccessible: ({ registrationData }) =>
      registrationData.eligibility?.isEligible === true,
    route: '/track-offering',
    blockBackNavigation: true,
  },
  {
    name: STEP_KEYS.NOT_ELIGIBLE,
    isRequired: ({ registrationData }) =>
      registrationData.eligibility?.isEligible === false,
    isCompleted: d(true),
    isAccessible: ({ registrationData }) =>
      registrationData.eligibility?.isEligible === false,
    route: '/not-eligible',
    blockBackNavigation: true,
    ignoreProgress: true,
  },
  {
    name: STEP_KEYS.CREATE_ACCOUNT,
    isRequired: d(true),
    isCompleted: ({ registrationData }) =>
      Boolean(registrationData.userData?.authToken),
    isAccessible: ({ registrationData }) =>
      Boolean(registrationData.selectedTracks?.length),
    route: '/create-account',
    blockBackNavigation: true,
  },
  {
    name: STEP_KEYS.SHIPPING,
    isRequired: ({ registrationData }) => 
      Boolean(registrationData.userData?.solutions?.some(s => s.hasShipping)),
    isCompleted: ({ hasFinishedShipping }) =>
      Boolean(hasFinishedShipping),
    isAccessible: ({ registrationData }) =>
      Boolean(registrationData.userData?.authToken),
    route: '/shipping',
    blockBackNavigation: true,
  },
  {
    name: STEP_KEYS.DOWNLOAD_APP,
    isRequired: d(true),
    isCompleted: d(false),
    isAccessible: ({ registrationData }) =>
      Boolean(registrationData.userData?.authToken),
    route: '/download-app',
  },
   {
    name: STEP_KEYS.CHECK_SMS,
    isRequired: d(true),
    isCompleted: d(false),
    isAccessible: ({ registrationData }) =>
      Boolean(registrationData.userData?.authToken),
    route: '/check-sms',
    ignoreProgress: true,
  },
  {
    name: STEP_KEYS.DOWNLOAD_APP_QR,
    isRequired: d(false),
    isCompleted: d(false),
    isAccessible: d(true),
    route: '/download-app-qr',
    ignoreProgress: true,
  },
];

const stepsConfigurationMap: Map<StepName, Step> = stepsConfiguration.reduce(
  (map, step) => map.set(step.name, step),
  new Map(),
);

export type FlowProgress = { current: number; total: number; percentage: number };

// Flow-aware progress: excludes steps that aren't required for this user (e.g.
// the PHQ-9/GAD-7 screeners when no BH condition was reported), unlike a fixed
// route-index/route-count calc, which jumps when a conditional step is skipped.
// Exported standalone (not tied to a single useAppFlow(stepName) call) so
// Layout — which renders every route generically — can compute it too.
export const calculateFlowProgress = (
  registrationContext: ReturnType<typeof useRegistration>,
): FlowProgress => {
  const requiredSteps = stepsConfiguration.filter(
    stepConf => !stepConf.ignoreProgress && stepConf.isRequired(registrationContext),
  );
  const completedSteps = requiredSteps.filter(stepConf =>
    stepConf.isCompleted(registrationContext),
  );
  const total = requiredSteps.length;
  // DOWNLOAD_APP is unconditionally required and permanently isCompleted:false,
  // so it's always one guaranteed-incomplete slot and current can't reach
  // total+1 today — clamped anyway as a defensive bound against future
  // stepsConfiguration changes removing that guarantee.
  const current = Math.min(total, completedSteps.length + 1);

  return {
    current,
    total,
    percentage: Math.min(100, Math.round((current / total) * 100)),
  };
};

export type UseAppFlowOptions = {
  autoNavigateOnComplete?: boolean;
  onBackNavigationBlocked?: () => void;
};

export const useAppFlow = (
  stepName: StepName,
  options: UseAppFlowOptions = {}
) => {
  const { autoNavigateOnComplete = false, onBackNavigationBlocked } = options;

  const step = stepsConfigurationMap.get(stepName);
  if (step === undefined) {
    throw new Error(`Step with name ${stepName} not found`);
  }

  const navigate = useNavigate();
  const registrationContext = useRegistration();
  const { isLoaded } = registrationContext;

  const isStepAccessible = step.isAccessible(registrationContext);
  const isStepRequired = step.isRequired(registrationContext);
  const isStepCompleted = step.isCompleted(registrationContext);

  const getNextStep = () =>
    stepsConfiguration.find(
      stepConf =>
        stepConf.name !== stepName && // Exclude current step from next step search
        stepConf.isRequired(registrationContext) &&
        !stepConf.isCompleted(registrationContext),
    );

  const getNextStepRoute = () => getNextStep()?.route;

  const navigateToNextStep = () => {
    const nextStepRoute = getNextStepRoute();
    if (!nextStepRoute) {
      return;
    }
    navigate(nextStepRoute);
  };

  // Block back navigation everywhere except Medicare Coverage
  const blocker = useBlocker(
    ({ historyAction }) =>
      stepName !== STEP_KEYS.MEDICARE_COVERAGE && historyAction === 'POP'
  );

  // Handle back navigation blocking
  useEffect(() => {
    if (blocker.state === 'blocked' && onBackNavigationBlocked) {
      onBackNavigationBlocked();
    }
  }, [blocker.state, onBackNavigationBlocked]);

  // Redirect if step is not accessible (wait for context to load from localStorage first)
  useEffect(() => {
    // Don't check accessibility until context has loaded
    if (!isLoaded) return;

    if (!isStepAccessible) {
      navigate('/');
    }
  }, [isStepAccessible, navigate, isLoaded]);

  // Auto-navigate on completion
  useEffect(() => {
    if (autoNavigateOnComplete && isStepCompleted) {
      navigateToNextStep();
    }
  }, [isStepCompleted, autoNavigateOnComplete]);

  // Calculate progress
  const getProgress = () => calculateFlowProgress(registrationContext);

  return {
    isStepAccessible,
    isStepRequired,
    isStepCompleted,
    getNextStep,
    getNextStepRoute,
    navigateToNextStep,
    getProgress,
    blocker,
    resetBlocker: () => blocker.reset?.(),
  };
};
